import { motion } from "framer-motion";
import { ArrowLeft, Download } from "lucide-react";
import { Link } from "react-router-dom";
import Crest from "../components/Crest";
import KushFlag from "../components/KushFlag";
import { PREAMBLE, CHARTER, DECLARATION_XIII, FINAL } from "../data/charter";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/charter`;

const go = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
};

function Article({ n, t, p, l }) {
  return (
    <article className="border-t border-[#00A651]/12 pt-7 mt-7" data-testid={`charter-article-${n}`}>
      <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">Article {n}</p>
      <h4 className="font-display font-bold text-lg text-[#F7F5F0] mt-2">{t}</h4>
      {p.map((para, i) => (
        <p key={i} className="text-sm sm:text-base text-[#8A99AD] leading-relaxed mt-3">
          {para}
        </p>
      ))}
      {l && (
        <ol className="mt-4 space-y-1.5 list-none">
          {l.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm text-[#8A99AD]">
              <span className="font-official text-[#00A651] shrink-0">{i + 1}.</span>
              {item}
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}

export default function CharterPage() {
  const nav = [
    { id: "charter-preamble", label: "Preamble" },
    ...CHARTER.map((c) => ({ id: `charter-${c.numeral}`, label: `${c.numeral}. ${c.title}` })),
    { id: "charter-xiii", label: "XIII. Declaration of African Unity" },
    { id: "charter-final", label: "Final Declaration" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0C0E] noise-overlay relative" data-testid="charter-page">
      <div className="sticky top-0 z-40 border-b border-[#00A651]/15 bg-[#0A0C0E]/85 backdrop-blur-xl">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto h-16 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2.5 font-official text-[11px] tracking-[0.2em] uppercase text-[#8A99AD] hover:text-[#00A651] transition-colors" data-testid="charter-back-link">
            <ArrowLeft className="w-4 h-4" /> The Union
          </Link>
          <div className="flex items-center gap-3">
            <Crest className="w-7 h-7" />
            <span className="hidden sm:block font-display font-extrabold text-sm text-[#F7F5F0]">THE CHARTER</span>
          </div>
          <a
            href={PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="charter-download-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#00A651]/40 text-[#00A651] font-display font-bold text-xs px-5 py-2.5 hover:bg-[#00A651]/10 transition-all duration-300"
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </a>
        </div>
      </div>

      <header className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-20 pb-14 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex justify-center">
            <KushFlag className="w-20 h-[54px] rounded-sm shadow-[0_0_32px_rgba(206,17,38,0.3)]" />
          </div>
          <p className="font-official text-[11px] tracking-[0.35em] uppercase text-[#00A651] mt-8">
            One Africa · One People · One Destiny · One Passport
          </p>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#F7F5F0] mt-6 leading-[1.02]" data-testid="charter-title">
            CHARTER OF THE
            <span className="block text-[#00A651]">UNITED STATES OF AFRICA</span>
          </h1>
          <p className="font-official text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#8A99AD] mt-6 max-w-2xl mx-auto leading-relaxed">
            A Pan-African charter for African unity, integration, free movement, the African diaspora and the Africa we want
          </p>
        </motion.div>
      </header>

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-24 grid lg:grid-cols-12 gap-12">
        <nav className="hidden lg:block lg:col-span-3">
          <div className="sticky top-28 space-y-2.5" data-testid="charter-nav">
            {nav.map((item) => (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                data-testid={`charter-nav-${item.id}`}
                className="block text-left font-official text-[10px] tracking-[0.15em] uppercase text-[#8A99AD] hover:text-[#00A651] transition-colors leading-relaxed"
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>

        <main className="lg:col-span-9 max-w-3xl">
          <section id="charter-preamble" className="scroll-mt-28">
            <h2 className="font-display font-extrabold text-2xl text-[#F7F5F0]">Preamble</h2>
            <p className="font-serif-display italic text-xl text-[#00A651] mt-6">{PREAMBLE.lead}</p>
            <div className="mt-6 space-y-4">
              {PREAMBLE.lines.map((line, i) => (
                <p key={i} className="text-sm sm:text-base text-[#8A99AD] leading-relaxed border-l-2 border-[#00A651]/25 pl-5">
                  {line}
                </p>
              ))}
            </div>
            <p className="text-sm sm:text-base text-[#F7F5F0] mt-8">{PREAMBLE.declare}</p>
            <div className="mt-4 font-display font-extrabold text-2xl sm:text-3xl text-[#00A651] space-y-1">
              {PREAMBLE.declaration.map((d) => (
                <p key={d}>{d}</p>
              ))}
            </div>
            <p className="text-sm sm:text-base text-[#8A99AD] leading-relaxed mt-8">{PREAMBLE.close}</p>
          </section>

          {CHARTER.map((c) => (
            <section key={c.numeral} id={`charter-${c.numeral}`} className="mt-20 scroll-mt-28" data-testid={`charter-section-${c.numeral}`}>
              <div className="flex items-baseline gap-5">
                <span className="font-display font-extrabold text-5xl text-outline-gold leading-none">{c.numeral}</span>
                <h2 className="font-display font-extrabold text-xl sm:text-2xl text-[#F7F5F0]">{c.title}</h2>
              </div>
              {c.articles.map((a) => (
                <Article key={a.n} {...a} />
              ))}
            </section>
          ))}

          <section id="charter-xiii" className="mt-20 scroll-mt-28" data-testid="charter-section-XIII">
            <div className="flex items-baseline gap-5">
              <span className="font-display font-extrabold text-5xl text-outline-gold leading-none">{DECLARATION_XIII.numeral}</span>
              <h2 className="font-display font-extrabold text-xl sm:text-2xl text-[#F7F5F0]">{DECLARATION_XIII.title}</h2>
            </div>
            <p className="text-sm sm:text-base text-[#F7F5F0] mt-8">{DECLARATION_XIII.intro}</p>
            <div className="mt-5 space-y-3">
              {DECLARATION_XIII.proclaim.map((line, i) => (
                <p key={i} className="text-sm sm:text-base text-[#8A99AD] leading-relaxed border-l-2 border-[#00A651]/25 pl-5">
                  {line}
                </p>
              ))}
            </div>
            <p className="text-sm sm:text-base text-[#F7F5F0] mt-8">{DECLARATION_XIII.workIntro}</p>
            <div className="mt-4 font-display font-extrabold text-lg sm:text-xl text-[#00A651] space-y-1.5">
              {DECLARATION_XIII.work.map((w) => (
                <p key={w}>{w}</p>
              ))}
            </div>
          </section>

          <section id="charter-final" className="mt-24 scroll-mt-28 text-center border-y border-[#00A651]/15 py-16" data-testid="charter-final">
            <p className="font-official text-[10px] tracking-[0.35em] uppercase text-[#8A99AD]">Final Declaration</p>
            <div className="mt-8 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl leading-snug">
              {FINAL.lines.map((line, i) => (
                <p key={line} className={i === FINAL.lines.length - 1 ? "text-[#CE1126]" : "text-[#00A651]"}>
                  {line}
                </p>
              ))}
            </div>
            <p className="text-sm sm:text-base text-[#8A99AD] leading-relaxed mt-8 max-w-xl mx-auto">{FINAL.bridge}</p>
            <div className="mt-6 font-serif-display italic text-lg text-[#F7F5F0] space-y-1">
              {FINAL.close.map((c) => (
                <p key={c}>{c}</p>
              ))}
            </div>
            <div className="mt-10 h-1.5 rounded-full overflow-hidden flex max-w-md mx-auto">
              <span className="flex-1 bg-[#009B4D]" />
              <span className="flex-1 bg-[#FFD700]" />
              <span className="flex-1 bg-[#CE1126]" />
            </div>
            <p className="font-official text-xs tracking-[0.4em] uppercase text-[#00A651] mt-8">{FINAL.sign}</p>
          </section>
        </main>
      </div>
    </div>
  );
}
